/**
 * Pending-handoff slice.
 *
 * Owns the family of one-shot "pending" signals drained by various consumers:
 * companion prompt, chat-image attach, sub-agent input, Marcus guided flow,
 * Scout research run, element-plan review, and Meta/GAds task-outcome filters.
 */

import type { StoreApi } from "zustand";
import type { AdsWorkspaceState } from "../useAdsWorkspaceStore.types";
import { requestCompanionDockAutoOpen } from "../adsCompanionEvents";

type SetState = StoreApi<AdsWorkspaceState>["setState"];
type GetState = StoreApi<AdsWorkspaceState>["getState"];

export type PendingHandoffActions = Pick<
  AdsWorkspaceState,
  | "requestCompanionPrompt"
  | "clearPendingCompanionPrompt"
  | "requestChatImageAttach"
  | "clearPendingChatImageAttachment"
  | "requestSubAgentInput"
  | "clearPendingSubAgentInput"
  | "setPendingMarcusGuidedFlow"
  | "clearPendingMarcusGuidedFlow"
  | "requestScoutResearchRun"
  | "clearPendingScoutResearchRun"
  | "requestOpenElementPlanReview"
  | "clearPendingElementPlanReviewOpen"
  | "requestMetaTasksOutcome"
  | "clearPendingMetaTasksOutcome"
  | "requestGadsTasksOutcome"
  | "clearPendingGadsTasksOutcome"
>;

export function createPendingHandoffSlice(
  set: SetState,
  _get: GetState,
): PendingHandoffActions {
  return {
    requestCompanionPrompt: (text) => set({ pendingCompanionPrompt: text }),
    clearPendingCompanionPrompt: () => set({ pendingCompanionPrompt: null }),

    requestChatImageAttach: (payload) =>
      set((state) => {
        requestCompanionDockAutoOpen("chat-injection");
        return {
          pendingChatImageAttachment: {
            nonce: (state.pendingChatImageAttachment?.nonce ?? 0) + 1,
            url: payload.url,
            filename: payload.filename,
            mediaType: payload.mediaType,
            kind: payload.kind ?? "image",
          },
          // If the user is at the chat list, bounce them into Marcus so the
          // attachment lands somewhere they can see.
          ...(state.companionViewLevel === "list"
            ? { companionViewLevel: "chat" as const, selectedChatAgent: null, activeSubAgent: null }
            : {}),
        };
      }),
    clearPendingChatImageAttachment: () => set({ pendingChatImageAttachment: null }),

    requestSubAgentInput: (agentId, text, options) =>
      set((state) => {
        requestCompanionDockAutoOpen("chat-injection");
        return {
          pendingSubAgentInput: {
            agentId,
            text,
            nonce: (state.pendingSubAgentInput?.nonce ?? 0) + 1,
            autoSubmit: options?.autoSubmit ?? false,
          },
        };
      }),
    clearPendingSubAgentInput: () => set({ pendingSubAgentInput: null }),

    setPendingMarcusGuidedFlow: (flow) => set({ pendingMarcusGuidedFlow: flow }),
    clearPendingMarcusGuidedFlow: (id) =>
      set((state) => {
        if (!state.pendingMarcusGuidedFlow) return state;
        if (id && state.pendingMarcusGuidedFlow.id !== id) return state;
        return { pendingMarcusGuidedFlow: null };
      }),

    requestScoutResearchRun: (payload) =>
      set((state) => {
        requestCompanionDockAutoOpen("agent-activation");
        const requestId = `${payload.productId ?? "scout"}:${Date.now()}`;
        return {
          pendingScoutResearchRun: {
            requestId,
            productId: payload.productId,
            productInput: payload.productInput,
          },
          // Reveal Scout in the running-agents list and bounce the user into
          // Scout's chat so they see the research run unfold.
          revealedSubAgents: state.revealedSubAgents.includes("scout")
            ? state.revealedSubAgents
            : [...state.revealedSubAgents, "scout"],
          selectedChatAgent: "scout",
          activeSubAgent: "scout",
          companionViewLevel: "chat",
        };
      }),
    clearPendingScoutResearchRun: (requestId) =>
      set((state) =>
        state.pendingScoutResearchRun?.requestId === requestId
          ? { pendingScoutResearchRun: null }
          : state,
      ),

    requestOpenElementPlanReview: () => set({ pendingElementPlanReviewOpen: true }),
    clearPendingElementPlanReviewOpen: () => set({ pendingElementPlanReviewOpen: false }),

    requestMetaTasksOutcome: (outcome) =>
      set((state) => ({
        pendingMetaTasksOutcome: {
          outcome,
          nonce: (state.pendingMetaTasksOutcome?.nonce ?? 0) + 1,
        },
      })),
    clearPendingMetaTasksOutcome: () => set({ pendingMetaTasksOutcome: null }),
    requestGadsTasksOutcome: (outcome) =>
      set((state) => ({
        pendingGadsTasksOutcome: {
          outcome,
          nonce: (state.pendingGadsTasksOutcome?.nonce ?? 0) + 1,
        },
      })),
    clearPendingGadsTasksOutcome: () => set({ pendingGadsTasksOutcome: null }),
  };
}
